import { useAccount } from "wagmi";
import useDappChain from "./useDappChain";
import {
  contractConfig,
  ledgerSupportedChains,
  swappingContractsConfig,
  vaultsContractConfig,
  vaultSwapContractsConfig,
  yieldVaultContractConfig,
  stableVaultContractConfig,
  cryptaniumVaultContractConfig,
} from "@config/contractsConfig";
import useLedgerLive from "./useLedgerLive";

const useDappConfig = () => {
  const { chain } = useAccount();
  const { unsupported } = useDappChain();

  const { isLedgerConnected } = useLedgerLive();

  const ledgerSupportedChainIds = ledgerSupportedChains.map((ch) => ch.id);

  const restakeEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) => !!contractConfig[+chaindId].nativeToken.isRestakeSupported
    )
    .map((chainId) => contractConfig[+chainId].chain);

  const withdrawEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) =>
        !!contractConfig[+chaindId].interactionPools.lrtWithdrawalManager
    )
    .map((chainId) => contractConfig[+chainId].chain);

  const vaultEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) => !!contractConfig[+chaindId].nativeToken.isVaultSupported
    )
    .map((chainId) => {
      return contractConfig[+chainId].chain;
    });

  const vaultWithdrawEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) =>
        !!contractConfig[+chaindId].nativeToken.isVaultWithdrawSupported
    )
    .map((chainId) => {
      return contractConfig[+chainId].chain;
    });

  const yieldVaultEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) =>
        !!contractConfig[+chaindId].nativeToken.isYieldVaultSupported
    )
    .map((chainId) => {
      return contractConfig[+chainId].chain;
    });

  const stableVaultEnabledChains = Object.keys(stableVaultContractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .map((chainId) => {
      return stableVaultContractConfig[+chainId].chain;
    });

  const cryptaniumVaultEnabledChains = Object.keys(
    cryptaniumVaultContractConfig
  )
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .map((chainId) => {
      return cryptaniumVaultContractConfig[+chainId].chain;
    });

  const claimELEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter((chaindId) => !!contractConfig[+chaindId].interactionPools.elToken)
    .map((chainId) => contractConfig[+chainId].chain);

  const claimScrollEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) =>
        !!contractConfig[+chaindId].interactionPools.scrollDistributor
    )
    .map((chainId) => contractConfig[+chainId].chain);

  const claimEnabledChains = [
    ...claimELEnabledChains,
    ...claimScrollEnabledChains,
  ];

  const claimBscEnabledChains = Object.keys(contractConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .filter(
      (chaindId) =>
        !!contractConfig[+chaindId].interactionPools
          .kernelAirdrop_S3V1_MerkleDistributorBsc
    )
    .map((chainId) => contractConfig[+chainId].chain);

  const swapEnabledChains = Object.keys(swappingContractsConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .map((chainId) => swappingContractsConfig[+chainId].chain);

  const vaultSwapEnabledChains = Object.keys(vaultSwapContractsConfig)
    .filter((chainId) =>
      isLedgerConnected
        ? !!ledgerSupportedChainIds.find((ch) => ch === +chainId)
        : true
    )
    .map((chainId) => vaultSwapContractsConfig[+chainId].chain);

  const isRestakeSupported =
    !unsupported && !!restakeEnabledChains.find((ch) => ch.id === chain?.id);
  const isSwapSupported =
    !unsupported && !!swapEnabledChains.find((ch) => ch.id === chain?.id);
  const isVaultSwapSupported =
    !unsupported && !!vaultSwapEnabledChains.find((ch) => ch.id === chain?.id);
  const isClaimSupported =
    !unsupported &&
    !![...claimEnabledChains, ...claimBscEnabledChains].find(
      (ch) => ch.id === chain?.id
    );
  const isClaimELSupported =
    !unsupported && !!claimELEnabledChains.find((ch) => ch.id === chain?.id);
  const isClaimScrollSupported =
    !unsupported &&
    !!claimScrollEnabledChains.find((ch) => ch.id === chain?.id);
  const isWithdrawSupported =
    !unsupported && !!withdrawEnabledChains.find((ch) => ch.id === chain?.id);
  const isVaultSupportedChain =
    !unsupported && !!vaultEnabledChains.find((ch) => ch.id === chain?.id);

  const isVaultWithdrawSupportedChain =
    !unsupported &&
    !!vaultWithdrawEnabledChains.find((ch) => ch.id === chain?.id);

  const isYieldVaultSupportedChain =
    !unsupported && !!yieldVaultEnabledChains.find((ch) => ch.id === chain?.id);

  const isStableVaultSupportedChain =
    !unsupported &&
    !!stableVaultEnabledChains.find((ch) => ch.id === chain?.id);

  const isCryptaniumVaultSupportedChain =
    !unsupported &&
    !!cryptaniumVaultEnabledChains.find((ch) => ch.id === chain?.id);

  const config = isRestakeSupported
    ? contractConfig[chain!.id]
    : contractConfig[restakeEnabledChains[0]?.id];

  const swapConfig = isSwapSupported
    ? swappingContractsConfig[chain!.id]
    : swappingContractsConfig[swapEnabledChains[0]?.id];

  const bridgeWrapConfig = { ...swapConfig, ...config };

  const vaultSwapConfig = isVaultSwapSupported
    ? vaultSwapContractsConfig[chain!.id]
    : vaultSwapContractsConfig[vaultSwapEnabledChains[0]?.id];

  const vaultConfig = isVaultSupportedChain
    ? vaultsContractConfig[chain!.id]
    : vaultsContractConfig[vaultEnabledChains[0]?.id];

  const yieldVaultConfig = isYieldVaultSupportedChain
    ? yieldVaultContractConfig[chain!.id]
    : yieldVaultContractConfig[yieldVaultEnabledChains[0]?.id];

  const stableVaultConfig = isStableVaultSupportedChain
    ? stableVaultContractConfig[chain!.id]
    : stableVaultContractConfig[stableVaultEnabledChains[0]?.id];

  const cryptaniumVaultConfig = isCryptaniumVaultSupportedChain
    ? cryptaniumVaultContractConfig[chain!.id]
    : cryptaniumVaultContractConfig[cryptaniumVaultEnabledChains[0]?.id];

  return {
    config,
    swapConfig,
    vaultSwapConfig,
    bridgeWrapConfig,
    vaultConfig,
    yieldVaultConfig,
    stableVaultConfig,
    cryptaniumVaultConfig,
    isRestakeSupported,
    isSwapSupported,
    isClaimSupported,
    isClaimELSupported,
    isClaimScrollSupported,
    isWithdrawSupported,
    isStableVaultSupportedChain,
    isCryptaniumVaultSupportedChain,
    restakeEnabledChains,
    claimEnabledChains,
    claimBscEnabledChains,
    claimELEnabledChains,
    swapEnabledChains,
    isVaultSupportedChain,
    isVaultWithdrawSupportedChain,
    isYieldVaultSupportedChain,
    withdrawEnabledChains,
    yieldVaultContractConfig,
    vaultsContractConfig,
    vaultEnabledChains,
    vaultSwapEnabledChains,
    yieldVaultEnabledChains,
    stableVaultEnabledChains,
    cryptaniumVaultEnabledChains,
    cryptaniumVaultContractConfig,
    isVaultSwapSupported,
  };
};

export default useDappConfig;
